/**
 * Utility functions for fetching user data for email notifications
 * This file centralizes user data fetching to avoid code duplication
 */

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType?: string;
  [key: string]: any;
}

/**
 * Fetch user data by organization membership ID
 * GET /organization-memberships/{id}
 * @param membershipId - Organization membership ID to fetch
 * @param token - Authentication token
 * @returns User data or null if not found
 */
const isValidUserLookupId = (membershipId: string | null | undefined): membershipId is string => {
  const id = String(membershipId ?? "").trim();
  return !!id && id !== "null" && id !== "undefined";
};

export const fetchUserData = async (
  membershipId: string | null | undefined,
  token: string | null
): Promise<UserData | null> => {
  if (!isValidUserLookupId(membershipId) || !token) {
    return null;
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/organization-memberships/${membershipId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.warn(
        `Failed to fetch user data for membershipId: ${membershipId}`,
        response.statusText
      );
      return null;
    }

    const data = await response.json();
    const personal = data?.addresses?.personal || {};
    return {
      ...data,
      id: data?.id || data?._id || data?.membership_id || membershipId,
      email: data?.email || "",
      firstName: data?.firstName || personal.first_name || "",
      lastName: data?.lastName || personal.last_name || "",
      licenseType: data?.licenseType || data?.local_license_type || "",
    };
  } catch (error) {
    console.error(`Error fetching user data for membershipId: ${membershipId}`, error);
    return null;
  }
};

/**
 * Fetch multiple users' data in parallel
 * @param membershipIds - Array of organization membership IDs to fetch
 * @param token - Authentication token
 * @returns Map of membershipId to UserData
 */
export const fetchMultipleUsersData = async (
  membershipIds: (string | null | undefined)[],
  token: string | null
): Promise<Map<string, UserData>> => {
  const userMap = new Map<string, UserData>();
  
  if (!token || membershipIds.length === 0) {
    return userMap;
  }

  // Filter out null/undefined and duplicates
  const validIds = Array.from(new Set(membershipIds.filter((id): id is string => !!id)));
  
  if (validIds.length === 0) {
    return userMap;
  }

  // Fetch all memberships in parallel
  const fetchPromises = validIds.map(async (membershipId) => {
    const userData = await fetchUserData(membershipId, token);
    if (userData) {
      userMap.set(membershipId, userData);
    }
    return userData;
  });

  await Promise.allSettled(fetchPromises);
  
  return userMap;
};

/**
 * Customer Group interface
 */
export interface CustomerGroup {
  _id: string;
  name: string;
  email: string;
  payment_after_days: number;
  allow_partial_pay: boolean;
  max_credit_limit: number;
  allowed_user_ids: Array<{
    user_id: string;
    name: string;
    email: string;
  }>;
  status: string;
  created_by: {
    id: string;
    name: string;
  };
  updated_by: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

/**
 * Fetch all customer groups
 * @param token - Authentication token
 * @returns Array of customer groups or empty array if error
 */
export const fetchCustomerGroups = async (
  token: string | null
): Promise<CustomerGroup[]> => {
  if (!token) {
    return [];
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch customer groups`, response.statusText);
      return [];
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`Error fetching customer groups`, error);
    return [];
  }
};

/**
 * Get all unique emails from customer groups' allowed_user_ids
 * @param token - Authentication token
 * @returns Array of unique email addresses
 */
export const getCustomerGroupEmails = async (
  token: string | null
): Promise<string[]> => {
  const groups = await fetchCustomerGroups(token);
  const emailSet = new Set<string>();
  
  groups.forEach((group) => {
    if (Array.isArray(group.allowed_user_ids)) {
      group.allowed_user_ids.forEach((user) => {
        if (user?.email && user.email.trim()) {
          emailSet.add(user.email.trim());
        }
      });
    }
  });
  
  return Array.from(emailSet);
};

/**
 * Get customer group users with their data (email and name)
 * @param token - Authentication token
 * @returns Array of user info objects with email and name
 */
export const getCustomerGroupUsers = async (
  token: string | null
): Promise<Array<{ email: string; name: string; userId: string }>> => {
  const groups = await fetchCustomerGroups(token);
  const userMap = new Map<string, { email: string; name: string; userId: string }>();
  
  groups.forEach((group) => {
    if (Array.isArray(group.allowed_user_ids)) {
      group.allowed_user_ids.forEach((user) => {
        if (user?.email && user.email.trim() && user?.user_id) {
          const email = user.email.trim();
          if (!userMap.has(email)) {
            userMap.set(email, {
              email: email,
              name: user.name || email.split("@")[0],
              userId: user.user_id,
            });
          }
        }
      });
    }
  });
  
  return Array.from(userMap.values());
};

/**
 * Fetch customer group by domain
 * @param supplierId - Supplier ID (optional, can be empty string)
 * @param domain - Domain name (e.g., "warehouse")
 * @param token - Authentication token
 * @returns Customer group data or null if not found
 */
export const fetchCustomerGroupByDomain = async (
  supplierId: string | null | undefined,
  domain: string,
  token: string | null
): Promise<CustomerGroup | null> => {
  if (!domain || !token) {
    return null;
  }

  try {
    // Build URL - supplierId might be needed as query parameter or in path, adjust based on actual API
    const baseUrl = `${process.env.REACT_APP_API_URL}/customer-groups/organizations/${supplierId}/by-domain/${domain}`;
    const url = supplierId ? `${baseUrl}` : baseUrl;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch customer group by domain: ${domain}`, response.statusText);
      return null;
    }

    const data = await response.json();
    return data || null;
  } catch (error) {
    console.error(`Error fetching customer group by domain: ${domain}`, error);
    return null;
  }
};

/**
 * Parent hierarchy response interface
 */
export interface ParentHierarchy {
  parent_chain?: Array<{
    id?: string;
    _id?: string;
    name: string;
    license_type: string;
  }>;
}

/**
 * Resolve parent signage firm org from session cookies.
 * GET /parent-hierarchy has been removed — organizationId is the firm org for team members.
 */
export const fetchParentHierarchy = async (
  _token?: string | null
): Promise<{ parentSignageId: string | null; parentSignageName: string | null } | null> => {
  try {
    // Lazy import to avoid circular deps with salesOrderApi in some bundles
    const { getAuthCookie } = await import("./authCookies");
    const parentSignageId = String(getAuthCookie("organizationId") || "").trim() || null;
    if (!parentSignageId) return null;
    return {
      parentSignageId,
      parentSignageName: null,
    };
  } catch {
    return null;
  }
};

/**
 * Office Team Member interface
 */
export interface OfficeTeamMember {
  _id: string;
  vid?: number;
  email: string;
  userType: string;
  firstName: string;
  lastName: string;
  phone1?: string;
  phone2?: string;
  jobTitle?: string;
  licenseType: string;
  status: string;
  offices?: Array<{
    _id: string;
    officeName: string;
    officeType?: string;
    officeAddress?: any;
    status: boolean;
  }>;
  warehouses?: any;
  firmDetails?: any;
  agentDetails?: any;
  installerInfo?: any;
  [key: string]: any;
}

/**
 * Fetch brokerage team members by office ID
 * @param officeId - Office ID to fetch team members for
 * @param token - Authentication token
 * @returns Array of team member data or empty array if error
 */
export const fetchOfficeTeamMembers = async (
  officeId: string | null | undefined,
  token: string | null
): Promise<OfficeTeamMember[]> => {
  if (!officeId || !token) {
    return [];
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/office/${officeId}/team-members`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 400) {
        console.warn(`Invalid office ID: ${officeId}`);
        return [];
      }
      if (response.status === 404) {
        console.warn(`Office not found: ${officeId}`);
        return [];
      }
      console.warn(`Failed to fetch office team members for officeId: ${officeId}`, response.statusText);
      return [];
    }

    const data = await response.json();
    return Array.isArray(data?.data) ? data.data : [];
  } catch (error) {
    console.error(`Error fetching office team members for officeId: ${officeId}`, error);
    return [];
  }
};

/**
 * Get all unique emails from office team members
 * @param officeId - Office ID to fetch team members for
 * @param token - Authentication token
 * @returns Array of unique email addresses
 */
export const getOfficeTeamMemberEmails = async (
  officeId: string | null | undefined,
  token: string | null
): Promise<string[]> => {
  const teamMembers = await fetchOfficeTeamMembers(officeId, token);
  const emailSet = new Set<string>();
  
  teamMembers.forEach((member) => {
    if (member?.email && member.email.trim()) {
      emailSet.add(member.email.trim());
    }
  });
  
  return Array.from(emailSet);
};
